import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import { formatNumber } from "accounting"
import { useTranslation } from "react-i18next"

import { INCOGNITO_MASK } from "../../../Components/Private"
import { useIncognito } from "../../../hooks/useIncognito"
import { formatCurrency } from "../../../Utils/utils"

import "./Sankey.less"

// Rounding "% of total" to a whole number reads as identical shares (100% / 0%) for nodes
// that are actually 99.8% / 0.2% of a large total - one decimal place keeps that visible.
export const roundPct = (value, total) => (total > 0 ? Math.round((value / total) * 1000) / 10 : 0)

// Hand-rolled SVG Sankey. Visually identical to the Recover Analytics one, but the
// layout that recharts used to compute (node boxes + ribbon geometry) is done here
// so prod does not need a second charting library alongside chart.js.
//
// Data contract (same as Recover): nodes are { name, color, segment } and links are
// { source, target, value } where source/target are indices into nodes.
//
// Why imperative hover highlighting: setting React state on mouse move would
// re-render every ribbon. Instead each node/link carries a data-* attribute and we
// toggle opacity directly on the DOM; enter/leave are coalesced into one
// requestAnimationFrame so sliding between adjacent ribbons does not shimmer.

const NODE_WIDTH = 12
const NODE_RADIUS = 4
// right is recomputed per-render (see measureLastColumnWidth) to fit whatever the last
// column's labels actually need; this is only the fallback before that ever runs.
const MARGIN = { top: 24, right: 184, bottom: 32, left: 0 }
// Floor for the measured right margin, so a chart with very short outcome names (or a
// font that hasn't loaded yet) never collapses the label column to near-zero.
const MIN_RIGHT_MARGIN = 60
// Gap between a bar's right edge and the start of the widest label that clears it.
const TEXT_GAP = 16
// The name line wraps onto a second line past this width ("Already a chargeback" etc.)
// instead of running wide and forcing a much bigger right margin.
const NAME_MAX_LINE_WIDTH = 110
const NAME_LINE_HEIGHT = 18
const MAX_NAME_LINES = 2

// A node's label is three lines ("<alerts> | <volume>" / "<name>" / "<pct> of total"), and
// it is centred on the node's bar. A node carrying two alerts is barely a pixel tall, so
// nothing but the gap between bars keeps two labels apart: NODE_PADDING has to clear a
// whole label, and NODE_SLOT (which sets the chart height) has to clear the padding too.
// Derive both from LABEL_HEIGHT so a taller label can never silently start overlapping -
// including the extra line a wrapped name can take, reserved for every node whether or
// not its own name actually wraps (a per-node variable gap would need a second layout pass).
const LABEL_HEIGHT = 56 + (MAX_NAME_LINES - 1) * NAME_LINE_HEIGHT
const NODE_PADDING = LABEL_HEIGHT + 8
const NODE_SLOT = NODE_PADDING + 36
const BASE_HEIGHT = 420

// Label text colours, theme-keyed (SVG text is painted in JS, so it cannot read
// Less tokens; both values are on-palette).
const labelColors = (dark) => ({
  primary: dark ? "#FCFCFC" : "#27272A", // Neutral50 / Neutral800
  muted: dark ? "#A1A1AA" : "#52525B" // Neutral400 / Neutral600 (AA on white card)
})

// The separator between the count and the volume. The root and network labels are drawn on
// top of their ribbons, so a fixed hairline colour would read on the card and disappear
// over the blue. Fading the label's own muted colour keeps it a hair either way.
const DIVIDER_OPACITY = 0.45

// The last column's labels have nothing to their right but MARGIN.right, sized to fit
// the widest one ("Already a chargeback"). Anchoring them to the bar's own right edge
// (like every other column) leaves whatever slack that label doesn't need dangling
// empty on the right; anchoring them to the chart's right edge instead keeps that
// same reserved width but flush-right, so short labels stop looking off-balance.
const LABEL_EDGE_PADDING = 8

// Real font metrics via a single shared canvas 2D context, so the label column reserves
// only what its own content needs instead of a fixed width sized for the longest string
// ever seen ("Already a chargeback") - a filtered mobile view with short outcome names
// gets its bars pulled much closer to the text.
let measureCtx = null
const textWidth = (text, font) => {
  if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d")
  measureCtx.font = font
  return measureCtx.measureText(text).width
}

// Greedy word-wrap against real font metrics, capped at maxLines - once the last allowed
// line is reached, remaining words just keep appending to it instead of wrapping further,
// so a name longer than maxLines allows overflows that line rather than dropping words.
const wrapLabel = (text, maxWidth, font, maxLines) => {
  const words = text.split(" ")
  const lines = []
  let current = ""
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word
    if (!current || lines.length === maxLines - 1 || textWidth(candidate, font) <= maxWidth) {
      current = candidate
    } else {
      lines.push(current)
      current = word
    }
  })
  if (current) lines.push(current)
  return lines
}

// Widest of the label's lines, at the exact font/weight each <tspan> renders with. Mobile
// renders a different set of lines than desktop (see the Sankey component), so its width
// has to be measured against that actual content or the reserved margin drifts out of
// sync with what's drawn.
const labelWidth = (node, value, pct, incognito, t, isMobile, unit) => {
  const count = incognito ? INCOGNITO_MASK : formatNumber(value, 0, ",", ".")
  const volume = incognito ? INCOGNITO_MASK : formatCurrency(node.volume ?? 0, true)
  const shownPct = incognito ? INCOGNITO_MASK : pct
  const nameLines = wrapLabel(node.name, NAME_MAX_LINE_WIDTH, "500 14px Inter", MAX_NAME_LINES)
  const nameWidth = Math.max(...nameLines.map((line) => textWidth(line, "500 14px Inter")))
  if (isMobile) {
    const countLine = textWidth(t("_count_ _unit_", { count, unit }), "400 14px Inter")
    const volumeLine = textWidth(
      t("_volume_, _percent_%", { volume, percent: shownPct }),
      "400 14px Inter"
    )
    return Math.max(nameWidth, countLine, volumeLine)
  }
  const countVolumeLine =
    textWidth(count, "700 16px Inter") +
    8 +
    textWidth("|", "400 14px Inter") +
    8 +
    textWidth(volume, "700 16px Inter")
  const percentLine = textWidth(t("_percent_% of total", { percent: shownPct }), "400 14px Inter")
  return Math.max(countVolumeLine, nameWidth, percentLine)
}

// Longest-path depth per node index, from the link structure.
const nodeDepths = (nodes, links) => {
  const parents = new Map()
  links.forEach((l) => {
    if (!parents.has(l.target)) parents.set(l.target, [])
    parents.get(l.target).push(l.source)
  })
  const depth = new Array(nodes.length).fill(-1)
  const resolve = (i, seen) => {
    if (depth[i] >= 0) return depth[i]
    const ps = parents.get(i)
    if (!ps || ps.length === 0) {
      depth[i] = 0
      return 0
    }
    if (seen.has(i)) return 0 // cycle guard
    seen.add(i)
    const d = 1 + Math.max(...ps.map((p) => resolve(p, seen)))
    depth[i] = d
    return d
  }
  nodes.forEach((_, i) => resolve(i, new Set()))
  return depth
}

// Root nodes never appear as a link target; the "% of total" denominator is the sum
// of flows leaving them.
const rootIndices = (data) => {
  const targets = new Set(data.links.map((l) => l.target))
  return new Set(data.nodes.map((_, i) => i).filter((i) => !targets.has(i)))
}

const isNodeActive = (index, hover, links) => {
  if (!hover) return true
  if (hover.kind === "node") return index === hover.index
  const link = links[hover.index]
  return link?.source === index || link?.target === index
}

const isLinkActive = (index, hover, links) => {
  if (!hover) return true
  if (hover.kind === "link") return index === hover.index
  const link = links[index]
  return link?.source === hover.index || link?.target === hover.index
}

// A node bar, rounded independently on its top and bottom edge - `<rect rx ry>` cannot
// do this (one radius for all four corners), and a merged bar needs its touching edge
// square while its free edge stays rounded.
const barPath = (x, y, w, h, rTop, rBottom) => {
  const top = Math.min(rTop, h / 2)
  const bottom = Math.min(rBottom, h / 2)
  return (
    `M${x},${y + top} ` +
    `Q${x},${y} ${x + top},${y} ` +
    `L${x + w - top},${y} ` +
    `Q${x + w},${y} ${x + w},${y + top} ` +
    `L${x + w},${y + h - bottom} ` +
    `Q${x + w},${y + h} ${x + w - bottom},${y + h} ` +
    `L${x + bottom},${y + h} ` +
    `Q${x},${y + h} ${x},${y + h - bottom} Z`
  )
}

// Node boxes + ribbon geometry. Node value is max(inflow, outflow) so a column that
// only receives still sizes correctly. The tightest column sets the shared
// value->pixel scale (`scale`); a node too small at that scale for its own label gets its
// own height topped up individually (`flowHeights`), without affecting its siblings.
const buildLayout = (data, width, height, total, incognito, t, isMobile, unit) => {
  const { nodes, links } = data
  if (!width || !nodes.length) return null

  const depths = nodeDepths(nodes, links)
  const maxDepth = Math.max(0, ...depths)

  const outSum = new Array(nodes.length).fill(0)
  const inSum = new Array(nodes.length).fill(0)
  links.forEach((l) => {
    outSum[l.source] += l.value
    inSum[l.target] += l.value
  })
  const values = nodes.map((_, i) => Math.max(outSum[i], inSum[i]))

  const lastColumnWidth = Math.max(
    0,
    ...nodes.map((node, i) => {
      if (depths[i] !== maxDepth) return 0
      const pct = roundPct(values[i], total)
      return labelWidth(node, values[i], pct, incognito, t, isMobile, unit)
    })
  )
  const margin = {
    ...MARGIN,
    right: Math.max(MIN_RIGHT_MARGIN, Math.ceil(lastColumnWidth) + TEXT_GAP + LABEL_EDGE_PADDING)
  }

  const innerW = width - margin.left - margin.right
  const innerH = height - margin.top - margin.bottom

  const columns = []
  depths.forEach((d, i) => {
    if (!columns[d]) columns[d] = []
    columns[d].push(i)
  })
  // Largest value first within each column, so a tiny node (e.g. a network with a single
  // alert) never lands wedged directly above one with hundreds - the incoming node order
  // is whatever the backend/toChartData produced, not sorted by size. Two exceptions pin a
  // specific node to the top of its column regardless of value: "Auto-refunded" in the
  // outcome column, and "RDR" in the network column (depth 1).
  columns.forEach((col, d) => {
    const isOutcome = d === maxDepth
    const isNetworkColumn = d === 1
    col.sort((a, b) => {
      if (isOutcome) {
        const aFirst = nodes[a].name === "Auto-refunded"
        const bFirst = nodes[b].name === "Auto-refunded"
        if (aFirst !== bFirst) return aFirst ? -1 : 1
      }
      if (isNetworkColumn) {
        const aFirst = nodes[a].name === "RDR"
        const bFirst = nodes[b].name === "RDR"
        if (aFirst !== bFirst) return aFirst ? -1 : 1
      }
      return values[b] - values[a]
    })
  })

  // The scale every node renders at by default, computed against a fixed reference height
  // (not the actual `innerH` below, which the caller may have already grown to fit a
  // topped-up node - see `flowHeights` next) so this baseline never shifts just because
  // some other node needed more room; only stays anchored to the data itself.
  //
  // A merge-eligible column (see the packed-geometry pass below) never actually spends
  // NODE_PADDING between its own siblings - only the outcome column does - so it must not
  // be sized here as if it did: that would needlessly shrink every merge-eligible column's
  // bars (and the label room built into them) to satisfy a gap requirement it doesn't have,
  // every time the outcome column happens to carry a lot of rows.
  const maxColNodes = Math.max(1, ...columns.map((col) => col.length))
  const baseHeight = Math.max(BASE_HEIGHT, MARGIN.top + MARGIN.bottom + maxColNodes * NODE_SLOT)
  const baseInnerH = baseHeight - MARGIN.top - MARGIN.bottom
  let scale = Infinity
  columns.forEach((col, d) => {
    const merge = d !== maxDepth
    const colTotal = col.reduce((sum, i) => sum + values[i], 0)
    const avail = merge ? baseInnerH : baseInnerH - (col.length - 1) * NODE_PADDING
    if (colTotal > 0 && avail > 0) scale = Math.min(scale, avail / colTotal)
  })
  if (!Number.isFinite(scale) || scale <= 0) scale = 1

  // A merge-eligible node that's a tiny sliver of its column's total (e.g. 2 of 61) can
  // still end up too short for its own label at `scale`. Top up just that node's own
  // height to LABEL_HEIGHT - its siblings that are already comfortably tall (e.g. a
  // network carrying 84% of the column) are left completely untouched, unlike scaling the
  // whole column up by a shared multiplier, which would stretch every node in it (even
  // ones that never needed the room) to fit the smallest one.
  const flowHeights = values.map((v, i) =>
    depths[i] === maxDepth ? Math.max(v * scale, 1) : Math.max(v * scale, 1, LABEL_HEIGHT)
  )

  const xStep = maxDepth > 0 ? (innerW - NODE_WIDTH) / maxDepth : 0

  // Desktop pins every column to the top. Mobile's filtered single-service view is always
  // exactly 2 columns (one source bar next to its outcomes) and centres the short source
  // column instead, so it doesn't sit flush at the top with empty space below it.
  //
  // With 3+ columns (mobile's "All alerts" option) this same per-column centring breaks:
  // the outer columns (root, outcomes) each have far less padding overhead than the middle
  // column(s), so they end up needing barely any offset while a middle column full of
  // small-vs-large networks needs a large one - columns drift out of line with each other
  // and ribbons swing down then back up instead of flowing straight across. There's no
  // per-column fix for that without a real barycentric layout pass, so 3+ columns just stay
  // top-aligned like desktop.
  const centerColumns = isMobile && maxDepth === 1
  const boxes = new Array(nodes.length)
  columns.forEach((col, d) => {
    const colHeight =
      col.reduce((sum, i) => sum + Math.max(values[i] * scale, 1), 0) +
      (col.length - 1) * NODE_PADDING
    let y = centerColumns ? margin.top + Math.max(0, (innerH - colHeight) / 2) : margin.top
    col.forEach((i) => {
      const h = Math.max(values[i] * scale, 1)
      boxes[i] = { x: margin.left + d * xStep, y, w: NODE_WIDTH, h, value: values[i], depth: d }
      y += h + NODE_PADDING
    })
  })

  // The bar/ribbon geometry a column actually draws with, kept separate from `box.y`
  // above (which exists purely to give each node's label clear vertical room via
  // NODE_PADDING). Every column except the final outcome column packs its nodes with
  // zero gap between them, each at its own `flowHeights[i]` - only a node that got topped
  // up above ends up taller than `values[i] * scale`, so a lopsided column grows just
  // enough for its cramped node(s), not uniformly for every node in it. The outcome
  // column keeps its real gaps (and its own label-driven centring) unconditionally, since
  // those bars are genuinely distinct results and go on splitting into their own coloured
  // "streams".
  columns.forEach((col, d) => {
    const merge = d !== maxDepth
    const flowColHeight = col.reduce((sum, i) => sum + flowHeights[i], 0)
    const colHeight = merge ? flowColHeight : flowColHeight + (col.length - 1) * NODE_PADDING
    let y = centerColumns ? margin.top + Math.max(0, (innerH - colHeight) / 2) : margin.top
    col.forEach((i, idx) => {
      const box = boxes[i]
      box.flowY = y
      box.flowH = flowHeights[i]
      // A rounded corner on a touching edge still reads as two separate bars pinched
      // together (each one curves away from the seam) - square that edge off instead so
      // the packed stack reads as a single bar with rounding only on its two free ends.
      box.radiusTop = merge && idx > 0 ? 0 : NODE_RADIUS
      box.radiusBottom = merge && idx < col.length - 1 ? 0 : NODE_RADIUS
      y += box.flowH + (merge ? 0 : NODE_PADDING)
    })
  })

  // Stack ribbons inside each node top-to-bottom in the same order the OTHER end's nodes
  // are actually plotted in - not the order links happen to appear in the data, which no
  // longer matches now that columns are sorted largest-first above. Stacking by raw link
  // order let a ribbon leave its source low (because its target sorted to the bottom of
  // the source's own link list) while arriving at its target high (because that target
  // itself sorted to the top of its column), crossing whatever ribbon took the opposite
  // path - visually a twisted band with a hole in the middle instead of a clean stack.
  // The two ends are computed independently since they stack against different columns.
  // `links`/`data.links` can be a seamless-immutable array (from Redux state) rather than
  // a plain one - spreading into a genuine Array up front, once, so every .map()/.sort()
  // below operates on plain arrays instead of immutable-map() returning another frozen
  // structure that .sort() then can't mutate.
  const linkList = [...links]

  // Ribbons are filled shapes (top edge + bottom edge), not a single centred stroke, so
  // stacked siblings can share an exact boundary instead of two independent curves that
  // start touching and then drift apart because they bend toward different targets.
  // Stacking against `flowY`/`flowH` (rather than the label-driven `box.y`/`box.h`) means
  // a merge-eligible node's own links already fill its packed, gap-free bar exactly, with
  // no separate "outer edge" to patch. Each end's width is that link's own share of
  // `values[node]` applied to `flowHeights[node]` (not `l.value * scale` directly) - so a
  // node's own links always sum to exactly its own (possibly topped-up) flowHeight,
  // tapering to match each side's own bar instead of reading a uniform width that no
  // longer matches one of the two bars it connects.
  const outOffset = new Array(nodes.length).fill(0)
  const sTops = new Array(linkList.length)
  const sBottoms = new Array(linkList.length)
  linkList
    .map((l, index) => ({ l, index }))
    .sort((a, b) => boxes[a.l.target].y - boxes[b.l.target].y)
    .forEach(({ l, index }) => {
      const s = boxes[l.source]
      const share = values[l.source] > 0 ? l.value / values[l.source] : 0
      const w = Math.max(share * flowHeights[l.source], 1)
      sTops[index] = s.flowY + outOffset[l.source]
      sBottoms[index] = sTops[index] + w
      outOffset[l.source] += w
    })

  const inOffset = new Array(nodes.length).fill(0)
  const tTops = new Array(linkList.length)
  const tBottoms = new Array(linkList.length)
  linkList
    .map((l, index) => ({ l, index }))
    .sort((a, b) => boxes[a.l.source].y - boxes[b.l.source].y)
    .forEach(({ l, index }) => {
      const tg = boxes[l.target]
      const share = values[l.target] > 0 ? l.value / values[l.target] : 0
      const w = Math.max(share * flowHeights[l.target], 1)
      tTops[index] = tg.flowY + inOffset[l.target]
      tBottoms[index] = tTops[index] + w
      inOffset[l.target] += w
    })

  const ribbons = links.map((l, index) => {
    const s = boxes[l.source]
    const targetBox = boxes[l.target]
    const sTop = sTops[index]
    const sBottom = sBottoms[index]
    const tTop = tTops[index]
    const tBottom = tBottoms[index]
    const sx = s.x + s.w
    const tx = targetBox.x
    const mid = (sx + tx) / 2
    const sy = (sTop + sBottom) / 2
    const ty = (tTop + tBottom) / 2
    const d =
      `M${sx},${sTop} C${mid},${sTop} ${mid},${tTop} ${tx},${tTop} ` +
      `L${tx},${tBottom} C${mid},${tBottom} ${mid},${sBottom} ${sx},${sBottom} Z`
    return { index, w: sBottom - sTop, sx, sy, tx, ty, d }
  })

  return { boxes, ribbons, maxDepth }
}

const Sankey = ({ data, theme, unit, isMobile, onNodeClick, onLinkClick }) => {
  const { t } = useTranslation()
  const incognito = useIncognito()
  const containerRef = useRef(null)
  const rafRef = useRef(null)
  const pendingRef = useRef(null)
  const [width, setWidth] = useState(0)
  const [tip, setTip] = useState(null)
  const tipRef = useRef(null)

  const dark = theme === "dark"
  const colors = labelColors(dark)
  const resolvedUnit = unit || t("alerts")
  const displayCount = (value) => (incognito ? INCOGNITO_MASK : formatNumber(value, 0, ",", "."))
  const displayVolume = (value) => (incognito ? INCOGNITO_MASK : formatCurrency(value ?? 0, true))

  // Lighter at rest, theme-keyed: overlapping translucent ribbons otherwise compound
  // into a solid block and the bars stop reading as anchors. Hover lifts the active
  // ribbon back to full opacity. Kept low enough that a node's own label - which sits
  // directly over several converging ribbons right next to the source bar - stays
  // readable even where three or four of them stack on top of each other.
  const stopOpacity = dark ? 0.3 : 0.32

  const total = useMemo(() => {
    const roots = rootIndices(data)
    return data.links.filter((l) => roots.has(l.source)).reduce((sum, l) => sum + l.value, 0)
  }, [data])

  // Height scales with the busiest column so the labels never overlap, and grows further
  // still if any node would otherwise be too cramped for its own label at that height's
  // scale - mirrors buildLayout's own `flowHeights` top-up so the canvas ends up tall
  // enough for whatever that decides each column needs, without this height itself
  // feeding back into a bigger `scale` (which would inflate every other node right along
  // with the cramped one).
  const height = useMemo(() => {
    const depths = nodeDepths(data.nodes, data.links)
    const maxDepth = Math.max(0, ...depths)
    const outSum = new Array(data.nodes.length).fill(0)
    const inSum = new Array(data.nodes.length).fill(0)
    data.links.forEach((l) => {
      outSum[l.source] += l.value
      inSum[l.target] += l.value
    })
    const values = data.nodes.map((_, i) => Math.max(outSum[i], inSum[i]))
    const columns = []
    depths.forEach((d, i) => {
      if (!columns[d]) columns[d] = []
      columns[d].push(i)
    })

    const maxColNodes = Math.max(1, ...columns.map((col) => col.length))
    const baseHeight = Math.max(BASE_HEIGHT, MARGIN.top + MARGIN.bottom + maxColNodes * NODE_SLOT)
    const baseInnerH = baseHeight - MARGIN.top - MARGIN.bottom

    let scale = Infinity
    columns.forEach((col, d) => {
      const merge = d !== maxDepth
      const colTotal = col.reduce((sum, i) => sum + values[i], 0)
      const avail = merge ? baseInnerH : baseInnerH - (col.length - 1) * NODE_PADDING
      if (colTotal > 0 && avail > 0) scale = Math.min(scale, avail / colTotal)
    })
    if (!Number.isFinite(scale) || scale <= 0) scale = 1

    let required = baseHeight
    columns.forEach((col, d) => {
      if (d === maxDepth) return
      const colTotal = col.reduce((sum, i) => sum + Math.max(values[i] * scale, 1, LABEL_HEIGHT), 0)
      required = Math.max(required, colTotal + MARGIN.top + MARGIN.bottom)
    })

    return required
  }, [data])

  // Callback ref, not an effect: the chart returns null until the flow arrives, so an
  // effect with [] deps would attach to a container that was not mounted yet and the
  // width would stay 0 forever. Measure on mount, then track resizes.
  const observerRef = useRef(null)
  const setContainer = useCallback((node) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    containerRef.current = node
    if (!node) return
    setWidth(node.getBoundingClientRect().width)
    if (typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0
      setWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev))
    })
    ro.observe(node)
    observerRef.current = ro
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  const layout = useMemo(
    () => buildLayout(data, width, height, total, incognito, t, isMobile, resolvedUnit),
    [data, width, height, total, incognito, t, isMobile, resolvedUnit]
  )

  const paintHighlight = useCallback(
    (target) => {
      const root = containerRef.current
      if (!root) return
      const links = data.links
      root.querySelectorAll("[data-sankey-node]").forEach((el) => {
        const idx = Number(el.dataset.sankeyNode)
        el.style.opacity = isNodeActive(idx, target, links) ? "1" : "0.3"
      })
      root.querySelectorAll("[data-sankey-link]").forEach((el) => {
        const idx = Number(el.dataset.sankeyLink)
        el.style.opacity = isLinkActive(idx, target, links) ? "1" : "0.12"
      })
    },
    [data.links]
  )

  // Coalesce enter/leave pairs into a single rAF so moving between adjacent ribbons
  // does not briefly reset everything to full opacity (a visible shimmer).
  const scheduleHighlight = useCallback(
    (target) => {
      pendingRef.current = target
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        paintHighlight(pendingRef.current)
      })
    },
    [paintHighlight]
  )

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    },
    []
  )

  // The box sits right of the cursor; where that would run past the chart's right edge (the last
  // column's nodes) it flips to the cursor's left, so it never leaves the card or the screen.
  useLayoutEffect(() => {
    const el = tipRef.current
    if (!el || !tip) return
    el.classList.toggle("dashboard-sankey_tooltip--left", tip.x + 12 + el.offsetWidth > width)
  }, [tip, width])

  const showTip = (e, content) => {
    const box = containerRef.current?.getBoundingClientRect()
    if (!box) return
    setTip({ x: e.clientX - box.left, y: e.clientY - box.top, content })
  }

  if (total === 0) return null

  return (
    <div ref={setContainer} className="dashboard-sankey" style={{ height }}>
      {layout && (
        <svg width="100%" height={height} role="img" aria-label={t("Alert flow")}>
          <defs>
            {layout.ribbons.map((r) => {
              const link = data.links[r.index]
              return (
                <linearGradient
                  key={r.index}
                  id={`dashboard-sankey-link-${r.index}`}
                  x1={r.sx}
                  x2={r.tx}
                  y1={0}
                  y2={0}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop
                    offset="0%"
                    stopColor={data.nodes[link.source].color}
                    stopOpacity={stopOpacity}
                  />
                  <stop
                    offset="100%"
                    stopColor={data.nodes[link.target].color}
                    stopOpacity={stopOpacity}
                  />
                </linearGradient>
              )
            })}
          </defs>

          {layout.ribbons.map((r) => {
            const link = data.links[r.index]
            const source = data.nodes[link.source]
            const target = data.nodes[link.target]
            return (
              <g
                key={r.index}
                data-sankey-link={r.index}
                style={{
                  cursor: onLinkClick ? "pointer" : "default",
                  opacity: 1,
                  transition: "opacity 150ms ease"
                }}
                onMouseEnter={(e) => {
                  scheduleHighlight({ kind: "link", index: r.index })
                  showTip(e, {
                    title: `${source.name} → ${target.name}`,
                    value: link.value,
                    volume: link.volume
                  })
                }}
                onMouseMove={(e) =>
                  showTip(e, {
                    title: `${source.name} → ${target.name}`,
                    value: link.value,
                    volume: link.volume
                  })
                }
                onMouseLeave={() => {
                  scheduleHighlight(null)
                  setTip(null)
                }}
                onClick={() => onLinkClick?.(source, target, link.value)}
              >
                {/* Invisible wider hit area along the ribbon's centreline so thin ribbons
                    stay easy to hover/click even though the filled shape itself may only
                    be a couple of pixels tall. */}
                <path
                  d={`M${r.sx},${r.sy} C${(r.sx + r.tx) / 2},${r.sy} ${(r.sx + r.tx) / 2},${r.ty} ${r.tx},${r.ty}`}
                  stroke="transparent"
                  strokeWidth={Math.max(r.w, 24)}
                  fill="none"
                  style={{ pointerEvents: "stroke" }}
                />
                <path d={r.d} fill={`url(#dashboard-sankey-link-${r.index})`} stroke="none" />
              </g>
            )
          })}

          {data.nodes.map((node, i) => {
            const box = layout.boxes[i]
            if (!box) return null
            // A caller can pin a specific node's percentage instead of deriving it from this
            // chart's own root (see toChartData's pctOverrides in SankeySection.js) - the
            // mobile single-service view uses this so its network node reads its true share
            // of every network, not 100% of the filtered subset it anchors.
            const pct = node.pctOverride != null ? node.pctOverride : roundPct(box.value, total)
            const shownPct = incognito ? INCOGNITO_MASK : pct
            const isLastColumn = box.depth === layout.maxDepth
            // Desktop's outcome column has room to spare on the right, so it hugs that
            // edge (see the dynamic margin above). Mobile is narrow enough that hugging
            // the right edge instead pushes the text away from the bar it belongs to, so
            // it stays anchored to the bar like every other column, and every node's
            // (source and outcome alike) label leads with the name instead of the count.
            const labelX =
              isLastColumn && !isMobile ? width - LABEL_EDGE_PADDING : box.x + box.w + 10
            const labelAnchor = isLastColumn && !isMobile ? "end" : "start"
            // Centred on the bar's actual drawn position (`flowY`/`flowH`), not the
            // label-only `box.y`/`box.h` - those two tracks only match for the first node
            // in a merged column, and drift further apart for each node after it (every
            // sibling's `box.y` still carries a full NODE_PADDING gap that the packed
            // `flowY` never has), so a later node's label would end up floating well below
            // where its own bar/ribbon actually is.
            const nameLines = wrapLabel(
              node.name,
              NAME_MAX_LINE_WIDTH,
              "500 14px Inter",
              MAX_NAME_LINES
            )
            // A near-zero-value node (clamped to a 1px bar) sitting at the very top or
            // bottom of a column would otherwise centre its label half off the edge of
            // the canvas - keep it fully on screen instead.
            // LABEL_HEIGHT reserves room for a name that wraps to the full MAX_NAME_LINES -
            // most names ("RDR", "All alerts") don't, so centering against that worst-case
            // height leaves the actually-shorter block sitting above true centre. Centre
            // against what this node's label really renders instead; the reserved-room
            // constant is still what's used to top up flowHeights and to clamp below, since
            // that's a safe upper bound, not this node's own actual size.
            const actualLabelHeight =
              LABEL_HEIGHT - (MAX_NAME_LINES - nameLines.length) * NAME_LINE_HEIGHT
            const rawLabelY = (box.flowY ?? box.y) + ((box.flowH ?? box.h) - actualLabelHeight) / 2
            const labelY = Math.min(Math.max(rawLabelY, 4), height - LABEL_HEIGHT - 4)
            const activate = () => onNodeClick?.(node, i)
            return (
              <g
                key={node.segment ?? i}
                data-sankey-node={i}
                role={onNodeClick ? "button" : undefined}
                tabIndex={onNodeClick ? 0 : undefined}
                aria-label={onNodeClick ? `${node.name}: ${displayCount(box.value)}` : undefined}
                style={{
                  cursor: onNodeClick ? "pointer" : "default",
                  opacity: 1,
                  transition: "opacity 150ms ease"
                }}
                onMouseEnter={(e) => {
                  scheduleHighlight({ kind: "node", index: i })
                  showTip(e, { title: node.name, value: box.value, volume: node.volume })
                }}
                onMouseMove={(e) =>
                  showTip(e, { title: node.name, value: box.value, volume: node.volume })
                }
                onMouseLeave={() => {
                  scheduleHighlight(null)
                  setTip(null)
                }}
                onClick={activate}
                onKeyDown={(e) => {
                  if (onNodeClick && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault()
                    activate()
                  }
                }}
              >
                <path
                  d={barPath(
                    box.x,
                    box.flowY ?? box.y,
                    box.w,
                    box.flowH ?? box.h,
                    box.radiusTop ?? NODE_RADIUS,
                    box.radiusBottom ?? NODE_RADIUS
                  )}
                  fill={node.color}
                />
                <text x={labelX} y={labelY} textAnchor={labelAnchor} dominantBaseline="hanging">
                  {isMobile ? (
                    <>
                      <tspan fill={node.color} fontSize="14" fontWeight="500">
                        {nameLines[0]}
                      </tspan>
                      {nameLines.slice(1).map((line, li) => (
                        <tspan
                          key={`name-${li}`}
                          x={labelX}
                          dy={NAME_LINE_HEIGHT}
                          fill={node.color}
                          fontSize="14"
                          fontWeight="500"
                        >
                          {line}
                        </tspan>
                      ))}
                      <tspan x={labelX} dy="20" fill={colors.muted} fontSize="14" fontWeight="400">
                        {t("_count_ _unit_", {
                          count: displayCount(box.value),
                          unit: resolvedUnit
                        })}
                      </tspan>
                      <tspan x={labelX} dy="20" fill={colors.muted} fontSize="14" fontWeight="400">
                        {t("_volume_, _percent_%", {
                          volume: displayVolume(node.volume),
                          percent: shownPct
                        })}
                      </tspan>
                    </>
                  ) : (
                    <>
                      <tspan fill={colors.primary} fontSize="16" fontWeight="700">
                        {displayCount(box.value)}
                      </tspan>
                      <tspan
                        dx="8"
                        fill={colors.muted}
                        fillOpacity={DIVIDER_OPACITY}
                        fontSize="14"
                        fontWeight="400"
                      >
                        |
                      </tspan>
                      <tspan dx="8" fill={colors.primary} fontSize="16" fontWeight="700">
                        {displayVolume(node.volume)}
                      </tspan>
                      <tspan
                        x={labelX}
                        dy="22"
                        fill={colors.primary}
                        fontSize="14"
                        fontWeight="500"
                      >
                        {nameLines[0]}
                      </tspan>
                      {nameLines.slice(1).map((line, li) => (
                        <tspan
                          key={`name-${li}`}
                          x={labelX}
                          dy={NAME_LINE_HEIGHT}
                          fill={colors.primary}
                          fontSize="14"
                          fontWeight="500"
                        >
                          {line}
                        </tspan>
                      ))}
                      <tspan x={labelX} dy="18" fill={colors.muted} fontSize="14" fontWeight="400">
                        {t("_percent_% of total", { percent: shownPct })}
                      </tspan>
                    </>
                  )}
                </text>
              </g>
            )
          })}
        </svg>
      )}

      {/* Dark tooltip box, matching the other charts (kept dark in both themes). */}
      {tip && (
        <div ref={tipRef} className="dashboard-sankey_tooltip" style={{ left: tip.x, top: tip.y }}>
          <div className="dashboard-sankey_tooltip-title">{tip.content.title}</div>
          <div className="dashboard-sankey_tooltip-value">
            {displayCount(tip.content.value)} {resolvedUnit}
          </div>
          {typeof tip.content.volume === "number" && (
            <div className="dashboard-sankey_tooltip-value">
              {displayVolume(tip.content.volume)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Sankey
